import { useId } from "react";
import { Button, Card } from "@/design-system/components/ui";
import { logButtonLabel } from "./outreachCopy";
import { OUTREACH_CARD_HEADING } from "./PulledFromCard";

/** What the panel says about the most recent LOG click from this tab. */
export type OutreachLogOutcome =
  | {
      status: "logged";
      summary: string;
      entryIds: string[];
      /** Set when an Undo left some entries behind; those ids stay undoable. */
      undoError?: string;
    }
  | { status: "undone" };

/**
 * Log — records what was sent as ordinary Communications entries (L6/G12).
 *
 * <p>ONE button, named by what is ticked, so a specialist who copied both
 * channels logs both in one click and cannot log one channel twice by clicking
 * two buttons. A channel is ticked for them when they copy it; they can untick
 * one they copied and did not send.</p>
 *
 * <p>🔴 THE CONFIRMATION SAYS WHAT WAS WRITTEN AND NOTHING ELSE. The tab is
 * standalone (G1): it does not move a status, a milestone or a follow-up, so
 * the only thing to confirm is the log entry — and the only thing to offer is
 * taking it back.</p>
 */
export function LogPanel({
  textTicked,
  emailTicked,
  textAvailable,
  emailAvailable,
  onToggleText,
  onToggleEmail,
  onLog,
  logging,
  logError,
  loggingOlderCopy = false,
  outcome,
  onUndo,
  undoing,
}: {
  textTicked: boolean;
  emailTicked: boolean;
  /** False while that channel cannot be logged — nothing to send, or a required field missing. */
  textAvailable: boolean;
  emailAvailable: boolean;
  onToggleText: (next: boolean) => void;
  onToggleEmail: (next: boolean) => void;
  onLog: () => void;
  logging: boolean;
  logError: string | null;
  /**
   * True when a ticked channel's card has changed since it was copied. LOG
   * records the copy (L7), so the panel says so rather than letting the reader
   * assume the card's current words are what will be logged.
   */
  loggingOlderCopy?: boolean;
  outcome: OutreachLogOutcome | null;
  onUndo: () => void;
  undoing: boolean;
}) {
  const textId = useId();
  const emailId = useId();
  const anything = textTicked || emailTicked;

  return (
    <Card data-testid="outreach-log-panel">
      <h3 className={OUTREACH_CARD_HEADING}>Log</h3>

      <div className="mt-space-3 flex flex-wrap items-center gap-x-space-4 gap-y-space-2">
        <label htmlFor={textId} className="flex items-center gap-space-2 text-small text-onyx">
          <input
            id={textId}
            type="checkbox"
            checked={textTicked}
            disabled={!textAvailable}
            onChange={(e) => onToggleText(e.target.checked)}
            data-testid="outreach-log-text-sent"
          />
          Text sent
        </label>
        <label htmlFor={emailId} className="flex items-center gap-space-2 text-small text-onyx">
          <input
            id={emailId}
            type="checkbox"
            checked={emailTicked}
            disabled={!emailAvailable}
            onChange={(e) => onToggleEmail(e.target.checked)}
            data-testid="outreach-log-email-sent"
          />
          Email sent
        </label>
        <Button
          onClick={onLog}
          disabled={!anything}
          loading={logging}
          data-testid="outreach-log-button"
        >
          {logButtonLabel(textTicked, emailTicked)}
        </Button>
      </div>

      {loggingOlderCopy && (
        <p className="mb-0 mt-space-2 text-caption text-gray-mid" data-testid="outreach-log-older-copy">
          The message changed after you copied it. LOG records the words you copied.
        </p>
      )}

      {logError && (
        <p
          role="alert"
          className="mb-0 mt-space-3 rounded-md border border-status-error bg-white-warm px-space-3 py-space-2 text-small text-onyx"
          data-testid="outreach-log-error"
        >
          Couldn&apos;t log it: {logError}
        </p>
      )}

      {outcome && (
        <div
          role="status"
          className="mt-space-3 grid gap-space-1 border-t border-border-light pt-space-3"
          data-testid="outreach-log-outcome"
        >
          {outcome.status === "undone" ? (
            <p className="m-0 text-small text-onyx">Undone.</p>
          ) : (
            <>
              <div className="flex flex-wrap items-center gap-space-2">
                <p className="m-0 text-small text-onyx">
                  Logged to Communications: {outcome.summary}.
                </p>
                <Button
                  variant="text"
                  size="sm"
                  onClick={onUndo}
                  disabled={undoing}
                  data-testid="outreach-log-undo"
                >
                  Undo
                </Button>
              </div>
              {outcome.undoError && (
                <p className="m-0 text-small text-status-error" data-testid="outreach-undo-error">
                  {outcome.undoError}
                </p>
              )}
            </>
          )}
        </div>
      )}
    </Card>
  );
}
